import React, { useState, useEffect, useRef } from "react";
import dataService from "../services/dataService";
import { useSearch } from "../context/SearchContext";
import {
  FaSearch,
  FaEllipsisV,
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaTrash
} from "react-icons/fa";
import { BsChevronDown } from "react-icons/bs";
import { IoEyeOutline } from "react-icons/io5";
import "../css/usermanagement.css";
import { toast } from "react-toastify";
import ConfirmModal from "../components/ConfirmModal";

const UserManagement = () => {
  const { searchQuery, handleSearch } = useSearch();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Show 7 items per page to fit strictly
  const [totalUsers, setTotalUsers] = useState(0);
  const [loadError, setLoadError] = useState(null);
  // Refreshing an already-rendered list, as opposed to the blocking first load
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Form state
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    first_name: "",
    last_name: "",
    email: "",
    role: "Viewer",
    status: "Active",
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [statusModalUser, setStatusModalUser] = useState(null); // user for activate/deactivate modal
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPaymentData, setUserPaymentData] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const filterRef = useRef(null);
  const Api = process.env.REACT_APP_BACKEND_URL;
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery.trim());

  // The search box is shared through SearchContext, so debounce it before
  // querying instead of firing a request on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // A new search or status filter always starts again from the first page
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter]);

  // Keeps the silent refresh interval on the query the user is actually looking at
  const queryRef = useRef({ page: 1, search: "", status: "All" });
  queryRef.current = { page: currentPage, search: debouncedSearch, status: statusFilter };

  const hasLoadedOnceRef = useRef(false);
  const requestIdRef = useRef(0);

  const modalStatesRef = useRef({ showAddForm, showConfirmModal, statusModalUser, showUserDetails });

  useEffect(() => {
    modalStatesRef.current = { showAddForm, showConfirmModal, statusModalUser, showUserDetails };
  }, [showAddForm, showConfirmModal, statusModalUser, showUserDetails]);

  const loadUsers = async (isSilent = false) => {
    const token = localStorage.getItem("access_token");

    // Only the very first load may blank the page. Every later query (typing in
    // the search box, changing page or filter) refreshes the table in place, so
    // the control bar is never unmounted and the search box keeps focus.
    if (!isSilent) {
      if (hasLoadedOnceRef.current) setIsRefreshing(true);
      else setLoading(true);
    }

    // Ignore a response that a newer query has already superseded, so slow
    // replies cannot overwrite the results of what is now in the search box.
    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestId === requestIdRef.current;

    // Search, status filter and paging all run on the server so the panel is not
    // limited to whatever happened to fit in the first response page.
    const { page, search, status } = queryRef.current;
    const baseUrl = Api.endsWith("/") ? Api : `${Api}/`;
    const params = new URLSearchParams({
      skip: String((page - 1) * itemsPerPage),
      limit: String(itemsPerPage),
    });
    if (search) params.append("search", search);
    if (status !== "All") params.append("status", status.toLowerCase());

    try {
      const response = await fetch(`${baseUrl}admin/users?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error(`Failed to load users (${response.status})`);
      const result = await response.json();
      if (!isCurrent()) return;
      const newData = result.data || [];

      // Deep compare to prevent unnecessary re-renders that cause "jerking"
      setUsers(prev => {
        if (JSON.stringify(prev) === JSON.stringify(newData)) return prev;
        return newData;
      });
      setTotalUsers(result.total || 0);
      setLoadError(null);
    } catch (error) {
      console.error("Error loading users:", error);
      if (!isSilent && isCurrent()) {
        setLoadError("Could not load users. Please try again.");
      }
    } finally {
      if (isCurrent()) {
        hasLoadedOnceRef.current = true;
        if (!isSilent) {
          setLoading(false);
          setIsRefreshing(false);
        }
      }
    }
  };

  const fetchPaymentHistory = async (userId) => {
    setPaymentLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const baseUrl = Api.endsWith('/') ? Api : `${Api}/`;
      const response = await fetch(`${baseUrl}admin/users/${userId}/payment-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUserPaymentData(data);
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);
    } finally {
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();

    // Stable interval that doesn't re-create itself
    const interval = setInterval(() => {
      const { showAddForm, showConfirmModal, statusModalUser, showUserDetails } = modalStatesRef.current;
      if (!showAddForm && !showConfirmModal && !statusModalUser && !showUserDetails) {
        loadUsers(true); // Silent update
      }
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Api, currentPage, itemsPerPage, debouncedSearch, statusFilter]);

  useEffect(() => {
    if (showAddForm || showConfirmModal || statusModalUser || showUserDetails) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showAddForm, showConfirmModal, statusModalUser, showUserDetails]);

  // Close filter dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("access_token");
    const isEditing = !!editingUser;

    if (isEditing) {
      // EDIT MODE: PATCH /admin/users/{user_id}
      try {
        const userId = editingUser.user_id || editingUser.id;
        const baseUrl = Api.endsWith("/") ? Api : `${Api}/`;
        const patchUrl = `${baseUrl}admin/users/${userId}`;

        const patchData = {
          user_name: formData.name,
          first_name: formData.first_name,
          last_name: formData.last_name,
          account_type: formData.role,
          is_active: formData.status === "Active",
        };

        const response = await fetch(patchUrl, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(patchData),
        });

        if (response.ok) {
          toast.success("User updated successfully");
          setShowAddForm(false);
          setEditingUser(null);
          setFormData({ name: "", email: "", role: "fan", status: "Active" });
          await loadUsers();
        } else {
          const result = await response.json();
          toast.error(result.detail || result.message || "Failed to update user");
        }
      } catch (error) {
        console.error("PATCH error:", error);
        toast.error("Something went wrong");
      }
      return;
    }

    // ADD MODE: POST new user
    try {
      const submissionData = {
        user_name: formData.name,
        email: formData.email,
        account_type: formData.role,
        is_status: formData.status === "Active" ? 1 : 0,
      };

      const response = await fetch(`${Api}admin/users`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      if (response.ok) {
        toast.success("User added successfully");
        setShowAddForm(false);
        setFormData({ name: "", email: "", role: "fan", status: "Active" });
        await loadUsers();
      } else {
        const result = await response.json();
        toast.error(result.detail || result.message || "Failed to add user");
      }
    } catch (error) {
      console.error("handleSubmit error:", error);
      toast.error("Something went wrong");
    }
  };

  const isUserActive = (user) => {
    const s = user.is_status ?? user.is_active ?? user.status ?? user.active;
    return s === true || s === 1 || s === "1" || s === "Active" || s === "active";
  };

  const toggleUserStatus = async (user) => {
    const token = localStorage.getItem("access_token");
    const isActive = isUserActive(user);
    const action = isActive ? "deactivate" : "activate";
    const userId = user.user_id || user.id;
    const baseUrl = Api.endsWith('/') ? Api : `${Api}/`;
    const url = `${baseUrl}admin/users/${userId}/${action}`;

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success(`User ${action}d successfully`);
        loadUsers();
      } else {
        const result = await response.json();
        toast.error(result.message || `Failed to ${action} user`);
      }
    } catch (error) {
      console.error(`Error ${action}ing user:`, error);
      toast.error(`Failed to ${action} user`);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    const isActive = isUserActive(user);
    setFormData({
      name: user.user_name || user.name || "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      role: user.account_type || user.role || "fan",
      status: isActive ? "Active" : "Inactive",
    });
    setShowAddForm(true);
  };

  const handleDelete = (user_id) => {
    setDeleteTarget(user_id);
    setShowConfirmModal(true);
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    const token = localStorage.getItem("access_token");
    setIsDeleting(true);
    try {
      const userDelete = await fetch(`${Api}admin/users/${deleteTarget}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await userDelete.json();
      if (result.success) {
        toast.success(result.message || "User deleted successfully");
        loadUsers();
        // Force page refresh after a brief delay
        setTimeout(() => window.location.reload(), 1000);
      } else {
        toast.error(result.message || "Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Failed to delete user");
    } finally {
      setIsDeleting(false);
      setShowConfirmModal(false);
      setDeleteTarget(null);
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingUser(null);
    setFormData({ name: "", email: "", role: "Viewer", status: "Active" });
  };

  // The API already applied the search, the status filter and the page window
  const currentItems = users || [];
  const totalPages = Math.max(1, Math.ceil(totalUsers / itemsPerPage));

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) setCurrentPage(pageNumber);
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="theme-loader"></div>
      </div>
    );
  }

  return (
    <div className="user-management-container">
      {/* Control Bar */}
      <div className="control-bar">
        <div className="search-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search"
            className="search-input-field"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <div className="action-buttons" ref={filterRef} style={{ position: "relative" }}>
          <button
            className="all-listing-btn"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
          >
            {statusFilter === "All" ? "All Listing" : statusFilter} <BsChevronDown size={12} />
          </button>

          {isFilterOpen && (
            <div className="filter-dropdown" style={{
              position: "absolute",
              top: "100%",
              right: "0",
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              marginTop: "10px",
              zIndex: 100,
              minWidth: "150px",
              overflow: "hidden"
            }}>
              {["All", "Active", "Inactive"].map((option) => (
                <button
                  key={option}
                  onClick={() => {
                    setStatusFilter(option);
                    setIsFilterOpen(false);
                    setCurrentPage(1); // Reset to first page when filtering
                  }}
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "12px 20px",
                    border: "none",
                    background: statusFilter === option ? "#fcebe8" : "transparent",
                    color: statusFilter === option ? "#ff2d2d" : "#333",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    fontWeight: statusFilter === option ? "600" : "400",
                    transition: "all 0.2s"
                  }}
                >
                  {option === "All" ? "All Listing" : option}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add User Form Modal/Overlay */}
      {showAddForm && (
        <div className="modal-overlay-user">
          <form onSubmit={handleSubmit} className="modal-form-user">
            <h3 className="modal-title-user">
              {editingUser ? "Edit User" : "Add New User"}
            </h3>
            <div className="form-grid-user">
              <input
                placeholder="Username"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                className="form-input-user"
              />
              <input
                placeholder="First Name"
                value={formData.first_name}
                onChange={(e) =>
                  setFormData({ ...formData, first_name: e.target.value })
                }
                className="form-input-user"
              />
              <input
                placeholder="Last Name"
                value={formData.last_name}
                onChange={(e) =>
                  setFormData({ ...formData, last_name: e.target.value })
                }
                className="form-input-user"
              />
              <input
                placeholder="Email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
                className="form-input-user"
              />
              {/* Role Selector - inline options, no native dropdown */}
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ fontSize: "0.8rem", color: "#888", fontWeight: "600" }}>Role</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {["admin", "creator", "athlete", "fan", "team"].map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setFormData({ ...formData, role: r })}
                      style={{
                        padding: "6px 14px",
                        borderRadius: "20px",
                        border: `1.5px solid ${formData.role === r ? "#ff2d2d" : "#ddd"}`,
                        background: formData.role === r ? "#fff0f0" : "transparent",
                        color: formData.role === r ? "#ff2d2d" : "#555",
                        fontWeight: formData.role === r ? "700" : "500",
                        fontSize: "0.82rem",
                        cursor: "pointer",
                        textTransform: "capitalize",
                        transition: "all 0.15s",
                      }}
                    >
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status Selector - inline options, no native dropdown */}
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ fontSize: "0.8rem", color: "#888", fontWeight: "600" }}>Status</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  {["Active", "Inactive"].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setFormData({ ...formData, status: s })}
                      style={{
                        padding: "6px 20px",
                        borderRadius: "20px",
                        border: `1.5px solid ${formData.status === s ? (s === "Active" ? "#166534" : "#991b1b") : "#ddd"}`,
                        background: formData.status === s ? (s === "Active" ? "#dcfce7" : "#fee2e2") : "transparent",
                        color: formData.status === s ? (s === "Active" ? "#166534" : "#991b1b") : "#555",
                        fontWeight: formData.status === s ? "700" : "500",
                        fontSize: "0.82rem",
                        cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer-user">
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  background: "transparent",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#ff2d2d",
                  color: "white",
                  cursor: "pointer",
                }}
              >
                {editingUser ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table Card */}
      <div className={`table-card${isRefreshing ? " is-refreshing" : ""}`}>
        {/* Horizontal Scroll Wrapper */}
        <div className="table-scroll-wrapper">
          {/* Header */}
          <div className="table-header">
            <span style={{ textAlign: "center" }}>S.No</span>
            <span>User</span>
            <span>Email</span>
            <span style={{ textAlign: "center" }}>Followers</span>
            <span style={{ textAlign: "center" }}>Posts</span>
            <span style={{ textAlign: "center" }}>Following</span>
            <span style={{ textAlign: "center" }}>Role</span>
            <span style={{ textAlign: "center" }}>Score</span>
            <span style={{ textAlign: "center" }}>Status</span>
            <span style={{ textAlign: "center" }}>Action</span>
          </div>

          {/* Rows */}
          {loadError ? (
            <div className="empty-state">
              <div className="empty-state-icon">!</div>
              <p>{loadError}</p>
            </div>
          ) : currentItems.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">&#128100;</div>
              <p>
                {debouncedSearch || statusFilter !== "All"
                  ? "No users match your search"
                  : "No users yet"}
              </p>
            </div>
          ) : (
            currentItems.map((user, index) => (
            <div
              key={user.user_id}
              className="table-row"
              style={{ alignItems: "center" }}
            >
              <span style={{ textAlign: "center" }}>{(currentPage - 1) * itemsPerPage + index + 1}</span>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <img
                  src={
                    user.profile_image
                      ? `${Api.replace(/\/$/, '')}${user.profile_image}`
                      : `https://ui-avatars.com/api/?name=${user.user_name}&background=ff2d2d&color=fff`
                  }
                  alt=""
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    textTransform: "uppercase",
                    fontWeight: "600",
                    fontSize: "0.8rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user.user_name || user.name || "Unknown"}
                </span>
              </div>
              <span
                style={{
                  fontSize: "0.75rem",
                  color: "#666",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {user.email || "N/A"}
              </span>
              <span style={{ textAlign: "center", fontWeight: "500" }}>
                {user.followers_count ?? user.followers ?? 0}
              </span>
              <span style={{ textAlign: "center", fontWeight: "500" }}>
                {user.total_posts ?? user.posts ?? 0}
              </span>
              <span style={{ textAlign: "center", fontWeight: "500" }}>
                {user.following_count ?? user.following ?? 0}
              </span>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <span
                  style={{
                    backgroundColor:
                      user.account_type === "admin" ? "#fee2e2" : "#f3f4f6",
                    color:
                      user.account_type === "admin" ? "#991b1b" : "#374151",
                    padding: "3px 10px",
                    borderRadius: "10px",
                    fontSize: "0.7rem",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user.account_type || user.role || "N/A"}
                </span>
              </div>
              <span
                style={{
                  textAlign: "center",
                  fontWeight: "700",
                  color: (user.user_score || user.score) ? "#ff2d2d" : "#999",
                }}
              >
                {user.user_score !== null && user.user_score !== undefined ? user.user_score : (user.score !== undefined ? user.score : "--")}
              </span>
              <div style={{ textAlign: "center" }}>
                <span
                  onClick={() => setStatusModalUser(user)}
                  style={{
                    backgroundColor: isUserActive(user) ? "#dcfce7" : "#fee2e2",
                    color: isUserActive(user) ? "#166534" : "#991b1b",
                    padding: "4px 12px",
                    borderRadius: "10px",
                    fontSize: "0.7rem",
                    fontWeight: "700",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    userSelect: "none",
                    transition: "all 0.2s",
                  }}
                >
                  {isUserActive(user) ? "Active" : "Banned"}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "center", gap: "2px" }}>
                <button
                  style={{
                    cursor: "pointer",
                    background: "transparent",
                    color: "#6366f1",
                    border: "none",
                    padding: "4px",
                    display: "flex",
                    alignItems: "center"
                  }}
                  onClick={() => {
                    setSelectedUser(user);
                    setShowUserDetails(true);
                    setUserPaymentData(null);
                    fetchPaymentHistory(user.user_id || user.id);
                  }}
                  title="View Details"
                >
                  <IoEyeOutline size={18} />
                </button>
                <button
                  style={{
                    cursor: "pointer",
                    background: "transparent",
                    color: "#000",
                    border: "none",
                    padding: "4px",
                    display: "flex",
                    alignItems: "center"
                  }}
                  onClick={() => handleEdit(user)}
                  title="Edit"
                >
                  <FaEdit size={16} />
                </button>
                <button
                  style={{
                    cursor: "pointer",
                    background: "transparent",
                    color: "#ff2d2d",
                    border: "none",
                    padding: "4px",
                    display: "flex",
                    alignItems: "center"
                  }}
                  onClick={() => handleDelete(user.user_id || user.id)}
                  title="Delete"
                >
                  <FaTrash size={16} />
                </button>
              </div>
            </div>
            ))
          )}
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="pagination-container">
        <button
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            padding: "8px 12px",
            border: "none",
            backgroundColor:
              currentPage === 1 ? "rgba(255,255,255,0.5)" : "white",
            borderRadius: "8px",
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
            color: currentPage === 1 ? "#999" : "#333",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FaChevronLeft size={12} />
        </button>

        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            onClick={() => paginate(i + 1)}
            style={{
              padding: "8px 14px",
              border: "none",
              backgroundColor: currentPage === i + 1 ? "#ff2d2d" : "white",
              color: currentPage === i + 1 ? "white" : "#333",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }}
          >
            {i + 1}
          </button>
        ))}

        <button
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            padding: "8px 12px",
            border: "none",
            backgroundColor:
              currentPage === totalPages ? "rgba(255,255,255,0.5)" : "white",
            borderRadius: "8px",
            cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            color: currentPage === totalPages ? "#999" : "#333",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FaChevronRight size={12} />
        </button>
      </div>

      {/* Custom Global Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        title="Delete User?"
        message="Are you sure you want to delete this user? All their associated data might be permanently removed."
        onClose={() => setShowConfirmModal(false)}
        onConfirm={executeDelete}
        isDeleting={isDeleting}
      />

      {/* Activate / Deactivate Confirmation Modal */}
      {statusModalUser && (
        <div style={{
          position: "fixed", inset: 0,
          backgroundColor: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 9999, padding: "1rem"
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "16px",
            padding: "2rem",
            maxWidth: "380px",
            width: "100%",
            textAlign: "center",
            boxShadow: "0 10px 40px rgba(0,0,0,0.15)"
          }}>
            {/* Icon */}
            <div style={{
              width: "60px", height: "60px", borderRadius: "50%",
              backgroundColor: isUserActive(statusModalUser) ? "#fef2f2" : "#f0fdf4",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 1rem",
              fontSize: "1.6rem"
            }}>
              {isUserActive(statusModalUser) ? "🚫" : "✅"}
            </div>

            <h3 style={{ margin: "0 0 0.5rem", fontSize: "1.15rem", fontWeight: "700", color: "#1a1a1a" }}>
              {isUserActive(statusModalUser) ? "Ban User?" : "Unban User?"}
            </h3>
            <p style={{ color: "#666", fontSize: "0.9rem", margin: "0 0 1.5rem" }}>
              {isUserActive(statusModalUser)
                ? `Are you sure you want to ban "${statusModalUser.user_name || statusModalUser.name || "this user"}"? They will not be able to access the app.`
                : `Are you sure you want to unban "${statusModalUser.user_name || statusModalUser.name || "this user"}"? They will regain access to the app.`
              }
            </p>

            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button
                onClick={() => setStatusModalUser(null)}
                style={{
                  padding: "10px 24px", borderRadius: "8px",
                  border: "1px solid #ddd", background: "transparent",
                  cursor: "pointer", fontWeight: "600", fontSize: "0.9rem"
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  toggleUserStatus(statusModalUser);
                  setStatusModalUser(null);
                }}
                style={{
                  padding: "10px 24px", borderRadius: "8px",
                  border: "none", background: isUserActive(statusModalUser) ? "#ff2d2d" : "#22c55e",
                  color: "#fff", cursor: "pointer", fontWeight: "600", fontSize: "0.9rem"
                }}
              >
                {isUserActive(statusModalUser) ? "Ban" : "Unban"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div
          className="modal-overlay-user"
          onClick={() => setShowUserDetails(false)}
          style={{ zIndex: 300 }}
        >
          <div
            className="modal-form-user"
            style={{ width: "450px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
              <h2 className="modal-title-user" style={{ margin: 0 }}>User Information</h2>
              <button
                onClick={() => setShowUserDetails(false)}
                style={{ background: "transparent", border: "none", fontSize: "1.5rem", cursor: "pointer", color: "#888" }}
              >&times;</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "0.5rem" }}>
                <img
                  src={
                    selectedUser.profile_image
                      ? `${Api.replace(/\/$/, '')}${selectedUser.profile_image}`
                      : `https://ui-avatars.com/api/?name=${selectedUser.user_name}&background=ff2d2d&color=fff`
                  }
                  alt=""
                  style={{ width: "65px", height: "65px", borderRadius: "50%", objectFit: "cover", border: "3px solid #ff2d2d", flexShrink: 0 }}
                />
                <div>
                  <h3 style={{ margin: 0, fontSize: "1.2rem", fontWeight: "700" }}>{selectedUser.user_name}</h3>
                  <p style={{ margin: 0, fontSize: "0.9rem", color: "#666" }}>{selectedUser.email}</p>
                </div>
              </div>

              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1.2rem",
                backgroundColor: "#f8f9fa",
                padding: "1.5rem",
                borderRadius: "16px",
                border: "1px solid #eee"
              }}>
                <div>
                  <label style={{ fontSize: "0.7rem", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Gender</label>
                  <p style={{ margin: "4px 0 0", fontWeight: "600", color: "#1a1a1a", textTransform: "capitalize" }}>{selectedUser.gender || "--"}</p>
                </div>
                <div>
                  <label style={{ fontSize: "0.7rem", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Date of Birth</label>
                  <p style={{ margin: "4px 0 0", fontWeight: "600", color: "#1a1a1a" }}>{selectedUser.dob || "--"}</p>
                </div>
                <div style={{ gridColumn: "span 2" }}>
                  <label style={{ fontSize: "0.7rem", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Joined On</label>
                  <p style={{ margin: "4px 0 0", fontWeight: "600", color: "#1a1a1a" }}>
                    {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : "--"}
                  </p>
                </div>
                <div>
                  <label style={{ fontSize: "0.7rem", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Account Type</label>
                  <p style={{ margin: "4px 0 0", fontWeight: "600", color: "#1a1a1a", textTransform: "capitalize" }}>{selectedUser.account_type || "--"}</p>
                </div>
                <div>
                  <label style={{ fontSize: "0.7rem", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Current Status</label>
                  <p style={{ margin: "4px 0 0", fontWeight: "700", color: isUserActive(selectedUser) ? "#22c55e" : "#ef4444" }}>
                    {isUserActive(selectedUser) ? "Active" : "Inactive"}
                  </p>
                </div>
              </div>

              {/* Payment & Plan Section */}
              <div style={{
                backgroundColor: "#f8f9fa",
                padding: "1.5rem",
                borderRadius: "16px",
                border: "1px solid #eee"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "1rem" }}>
                  <span style={{ fontSize: "1.2rem" }}>&#128179;</span>
                  <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: "700", color: "#1a1a1a" }}>Payment & Plan</h3>
                </div>

                {paymentLoading ? (
                  <p style={{ color: "#999", fontStyle: "italic", margin: "0.5rem 0" }}>Loading payment data...</p>
                ) : userPaymentData ? (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.2rem" }}>
                      <div>
                        <label style={{ fontSize: "0.7rem", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Plan Type</label>
                        <p style={{ margin: "4px 0 0" }}>
                          {userPaymentData.subscriptions && userPaymentData.subscriptions.length > 0 ? (
                            userPaymentData.subscriptions.map((sub, idx) => (
                              <span key={idx} style={{
                                display: "inline-flex", alignItems: "center",
                                padding: "4px 12px", borderRadius: "50px",
                                fontSize: "0.72rem", fontWeight: "600",
                                backgroundColor: sub.status === "active" ? "#dcfce7" : "#fee2e2",
                                color: sub.status === "active" ? "#16a34a" : "#991b1b",
                                marginRight: "6px", marginBottom: "4px"
                              }}>
                                {sub.plan} ({sub.subscription_type})
                              </span>
                            ))
                          ) : (
                            <span style={{ color: "#999", fontStyle: "italic" }}>No subscription</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <label style={{ fontSize: "0.7rem", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Paid</label>
                        <p style={{ margin: "4px 0 0", fontWeight: "700", color: "#ff2d2d", fontSize: "1.1rem" }}>${userPaymentData.total_paid?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}</p>
                      </div>
                      <div>
                        <label style={{ fontSize: "0.7rem", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Last Payment</label>
                        <p style={{ margin: "4px 0 0", fontWeight: "600", color: "#1a1a1a" }}>
                          {userPaymentData.payments && userPaymentData.payments.length > 0
                            ? new Date(userPaymentData.payments[0].created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                            : "--"
                          }
                        </p>
                      </div>
                      <div>
                        <label style={{ fontSize: "0.7rem", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Active Subscriptions</label>
                        <p style={{ margin: "4px 0 0", fontWeight: "600", color: "#1a1a1a" }}>{userPaymentData.active_subscriptions_count || 0}</p>
                      </div>
                    </div>

                    {/* Payment History */}
                    {userPaymentData.payments && userPaymentData.payments.length > 0 && (
                      <div style={{ marginTop: "1rem", paddingTop: "0.8rem", borderTop: "1px solid #eee" }}>
                        <label style={{ fontSize: "0.7rem", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "0.5rem" }}>Payment History</label>
                        <div style={{ maxHeight: "150px", overflowY: "auto" }}>
                          {userPaymentData.payments.map((payment, idx) => (
                            <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: idx < userPaymentData.payments.length - 1 ? "1px solid #eee" : "none" }}>
                              <div>
                                <p style={{ margin: 0, fontWeight: "600", fontSize: "0.85rem" }}>${payment.amount?.toFixed(2)}</p>
                                <p style={{ margin: 0, fontSize: "0.72rem", color: "#888" }}>{payment.product_id}</p>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <p style={{ margin: 0, fontSize: "0.78rem", color: "#666" }}>
                                  {new Date(payment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </p>
                                <span style={{
                                  fontSize: "0.65rem", fontWeight: "600",
                                  color: payment.status === "completed" ? "#16a34a" : "#991b1b",
                                  textTransform: "capitalize"
                                }}>{payment.status}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p style={{ color: "#999", fontStyle: "italic", margin: "0.5rem 0" }}>No payment data available</p>
                )}
              </div>

              <button
                onClick={() => setShowUserDetails(false)}
                style={{
                  marginTop: "0.5rem",
                  padding: "14px",
                  borderRadius: "12px",
                  backgroundColor: "#000",
                  color: "#fff",
                  border: "none",
                  fontWeight: "700",
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}
                onMouseOver={(e) => e.target.style.opacity = "0.9"}
                onMouseOut={(e) => e.target.style.opacity = "1"}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
